import React, { useEffect, useRef, useState } from "react";
import { Carousel } from "react-bootstrap";
import { Link } from "react-router-dom";
import WebHeader from "./WebHeader";
import WebFooter from "./WebFooter";

function Home() {
  const [counted, setCounted] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const domain = window.location.hostname;
    console.log("Fetched domain:", domain);
  }, []);

  // Counter animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !counted) setCounted(true); },
      { threshold: 0.4 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [counted]);

  const slides = [
    { image: "assets/images/auth/dream1.jpg" },
    { image: "assets/images/auth/dream2.jpg" },
    { image: "assets/images/auth/dream3.jpg" },
  ];

  const features = [
    { icon: "🤖", title: "AI-Powered Matching", desc: "Smart algorithms match your skills to the perfect role automatically." },
    { icon: "⚡", title: "Instant Shortlisting", desc: "Get shortlisted in minutes, not days. Real-time application tracking." },
    { icon: "🎯", title: "Precision Results", desc: "Only see jobs that truly match your profile. Zero irrelevant listings." },
    { icon: "🔒", title: "Verified Employers", desc: "Every company is verified. Safe, transparent, and trusted hiring." },
  ];

  const stats = [
    { num: "50K+", label: "Active Jobs" },
    { num: "12K+", label: "Companies" },
    { num: "200K+", label: "Candidates" },
    { num: "95%", label: "Match Rate" },
  ];

  const categories = [
    { icon: "💻", name: "Technology", count: "8,420 jobs" },
    { icon: "📊", name: "Finance", count: "3,210 jobs" },
    { icon: "🏥", name: "Healthcare", count: "4,800 jobs" },
    { icon: "🎨", name: "Design", count: "2,100 jobs" },
    { icon: "📱", name: "Marketing", count: "3,650 jobs" },
    { icon: "⚙️", name: "Engineering", count: "6,300 jobs" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap');

        :root {
          --blue: #2563eb; --blue-dark: #1d4ed8; --blue-light: #eff6ff;
          --navy: #0f172a; --slate: #475569; --muted: #94a3b8;
          --bg: #f8fafc; --white: #ffffff;
        }

        * { box-sizing: border-box; }
        body { font-family: 'DM Sans', sans-serif; }

        /* ── Hero Carousel ── */
        .hp-carousel-wrap { position: relative; }
        .hp-carousel-wrap .carousel-item img {
          max-height: 580px; object-fit: cover; width: 100%;
          filter: brightness(.72);
        }
        .hp-hero-overlay {
          position: absolute; inset: 0; z-index: 10;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(to bottom, rgba(15,23,42,.3) 0%, rgba(15,23,42,.6) 100%);
        }
        .hp-hero-content { text-align: center; padding: 0 20px; animation: fadeUp .8s ease both; }
        .hp-hero-tag {
          display: inline-block; background: rgba(37,99,235,.8); color: #fff;
          font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
          padding: 5px 16px; border-radius: 20px; margin-bottom: 18px;
          backdrop-filter: blur(6px);
        }
        .hp-hero-title {
          font-family: 'Syne', sans-serif; font-weight: 800;
          font-size: clamp(32px, 6vw, 62px); color: #fff;
          line-height: 1.12; margin-bottom: 16px; letter-spacing: -.5px;
        }
        .hp-hero-title span { color: #60a5fa; }
        .hp-hero-sub {
          font-size: clamp(15px, 2vw, 18px); color: rgba(255,255,255,.8);
          max-width: 540px; margin: 0 auto 32px; font-weight: 300; line-height: 1.65;
        }
        .hp-hero-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
        .hp-btn-primary {
          background: var(--blue); color: #fff; text-decoration: none;
          padding: 13px 30px; border-radius: 10px; font-weight: 700; font-size: 15px;
          transition: background .2s, transform .15s, box-shadow .2s;
          box-shadow: 0 4px 20px rgba(37,99,235,.4);
        }
        .hp-btn-primary:hover { background: var(--blue-dark); transform: translateY(-2px); box-shadow: 0 8px 28px rgba(37,99,235,.5); color: #fff; }
        .hp-btn-outline {
          background: rgba(255,255,255,.15); color: #fff; text-decoration: none;
          padding: 13px 30px; border-radius: 10px; font-weight: 600; font-size: 15px;
          border: 1.5px solid rgba(255,255,255,.4); backdrop-filter: blur(6px);
          transition: background .2s;
        }
        .hp-btn-outline:hover { background: rgba(255,255,255,.25); color: #fff; }

        /* ── Stats Bar ── */
        .hp-stats {
          background: var(--navy); padding: 28px 0;
        }
        .hp-stats-inner {
          max-width: 900px; margin: 0 auto; padding: 0 24px;
          display: flex; justify-content: space-around; flex-wrap: wrap; gap: 20px;
        }
        .hp-stat-item { text-align: center; }
        .hp-stat-num {
          font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 800;
          color: #60a5fa; display: block; line-height: 1;
        }
        .hp-stat-label { font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 4px; text-transform: uppercase; letter-spacing: .8px; }

        /* ── About Section ── */
        .hp-about { padding: 80px 0; background: var(--white); overflow: hidden; }
        .hp-about-img-wrap {
          position: relative; border-radius: 20px; overflow: hidden;
          box-shadow: 0 24px 60px rgba(0,0,0,.12);
        }
        .hp-about-img-wrap img { width: 100%; height: 420px; object-fit: cover; display: block; }
        .hp-about-img-badge {
          position: absolute; bottom: 20px; left: 20px;
          background: var(--blue); color: #fff; border-radius: 12px;
          padding: 12px 18px; font-weight: 700; font-size: 13px;
          box-shadow: 0 8px 24px rgba(37,99,235,.4);
        }
        .hp-about-img-badge span { display: block; font-size: 22px; font-weight: 800; }
        .hp-section-tag {
          display: inline-block; background: var(--blue-light); color: var(--blue);
          font-size: 12px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
          padding: 4px 14px; border-radius: 20px; margin-bottom: 14px;
        }
        .hp-section-title {
          font-family: 'Syne', sans-serif; font-weight: 800;
          font-size: clamp(24px, 3.5vw, 36px); color: var(--navy);
          line-height: 1.2; margin-bottom: 20px;
        }
        .hp-about-text { color: var(--slate); line-height: 1.75; font-size: 15px; margin-bottom: 14px; }
        .hp-check-list { list-style: none; padding: 0; margin: 20px 0 28px; }
        .hp-check-list li {
          display: flex; align-items: center; gap: 10px;
          font-size: 14px; color: var(--slate); padding: 6px 0; font-weight: 500;
        }
        .hp-check-list li::before {
          content: '✓'; width: 22px; height: 22px; background: #dcfce7; color: #16a34a;
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700; flex-shrink: 0;
        }

        /* ── Features ── */
        .hp-features { padding: 80px 0; background: var(--bg); }
        .hp-features-header { text-align: center; margin-bottom: 50px; }
        .hp-feature-card {
          background: var(--white); border-radius: 16px; padding: 32px 28px;
          border: 1.5px solid #e8eaf0; height: 100%;
          transition: box-shadow .25s, transform .2s, border-color .25s;
          position: relative; overflow: hidden;
        }
        .hp-feature-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, var(--blue), #60a5fa);
          transform: scaleX(0); transition: transform .3s; transform-origin: left;
        }
        .hp-feature-card:hover { box-shadow: 0 12px 40px rgba(37,99,235,.12); transform: translateY(-4px); border-color: #bfdbfe; }
        .hp-feature-card:hover::before { transform: scaleX(1); }
        .hp-feature-icon {
          width: 56px; height: 56px; background: var(--blue-light);
          border-radius: 14px; display: flex; align-items: center; justify-content: center;
          font-size: 26px; margin-bottom: 18px;
        }
        .hp-feature-title { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 17px; color: var(--navy); margin-bottom: 8px; }
        .hp-feature-desc { color: var(--slate); font-size: 14px; line-height: 1.65; }

        /* ── Categories ── */
        .hp-categories { padding: 80px 0; background: var(--white); }
        .hp-cat-card {
          background: var(--bg); border-radius: 14px; padding: 24px 20px;
          text-align: center; border: 1.5px solid #e8eaf0; cursor: pointer;
          transition: all .22s; text-decoration: none; display: block;
        }
        .hp-cat-card:hover { background: var(--blue); border-color: var(--blue); transform: translateY(-3px); box-shadow: 0 8px 28px rgba(37,99,235,.2); }
        .hp-cat-card:hover .hp-cat-name { color: #fff; }
        .hp-cat-card:hover .hp-cat-count { color: rgba(255,255,255,.75); }
        .hp-cat-icon { font-size: 32px; margin-bottom: 10px; display: block; }
        .hp-cat-name { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 15px; color: var(--navy); margin-bottom: 4px; }
        .hp-cat-count { font-size: 12px; color: var(--muted); font-weight: 500; }

        /* ── CTA Banner ── */
        .hp-cta {
          padding: 80px 0;
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1d4ed8 100%);
          position: relative; overflow: hidden; text-align: center;
        }
        .hp-cta::before {
          content: ''; position: absolute;
          width: 500px; height: 500px; border-radius: 50%;
          background: radial-gradient(circle, rgba(99,102,241,.2) 0%, transparent 70%);
          top: -150px; right: -100px;
        }
        .hp-cta::after {
          content: ''; position: absolute;
          width: 300px; height: 300px; border-radius: 50%;
          background: radial-gradient(circle, rgba(37,99,235,.15) 0%, transparent 70%);
          bottom: -80px; left: -60px;
        }
        .hp-cta-inner { position: relative; z-index: 1; }
        .hp-cta-title { font-family: 'Syne', sans-serif; font-weight: 800; font-size: clamp(26px, 4vw, 42px); color: #fff; margin-bottom: 14px; }
        .hp-cta-sub { color: #93c5fd; font-size: 16px; margin-bottom: 32px; max-width: 480px; margin-left: auto; margin-right: auto; }

        /* ── Animations ── */
        @keyframes fadeUp { from { opacity:0; transform:translateY(30px) } to { opacity:1; transform:translateY(0) } }
        .fade-up { animation: fadeUp .7s ease both; }
        .fade-up-2 { animation: fadeUp .7s .15s ease both; }
        .fade-up-3 { animation: fadeUp .7s .3s ease both; }

        @media (max-width: 768px) {
          .hp-about { padding: 50px 0; }
          .hp-features, .hp-categories, .hp-cta { padding: 50px 0; }
          .hp-about-img-wrap { margin-bottom: 32px; }
        }
      `}</style>

      <WebHeader />

      <main>

        {/* ── Hero Carousel ── */}
        <div className="hp-carousel-wrap">
          <Carousel fade indicators interval={4000} controls={false}>
            {slides.map((slide, i) => (
              <Carousel.Item key={i}>
                <img src={slide.image} alt={`slide-${i}`} />
              </Carousel.Item>
            ))}
          </Carousel>
          <div className="hp-hero-overlay">
            <div className="hp-hero-content">
              <div className="hp-hero-tag">🤖 AI-Powered Recruitment</div>
              <h1 className="hp-hero-title">
                Find Your <span>Dream Job</span><br />with AI Precision
              </h1>
              <p className="hp-hero-sub">
                Smart matching technology connects top talent with the right opportunities — faster, fairer, and more accurate than ever.
              </p>
              <div className="hp-hero-btns">
                <Link to="/jobs" className="hp-btn-primary">Browse Jobs →</Link>
                <Link to="/about" className="hp-btn-outline">Learn More</Link>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Bar ── */}
        <div className="hp-stats" ref={statsRef}>
          <div className="hp-stats-inner">
            {stats.map(({ num, label }) => (
              <div key={label} className="hp-stat-item">
                <span className="hp-stat-num">{num}</span>
                <div className="hp-stat-label">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── About Section ── */}
        <section className="hp-about">
          <div className="container">
            <div className="row align-items-center g-5">
              <div className="col-lg-6 fade-up">
                <div className="hp-about-img-wrap">
                  <img
                    src="https://static.vecteezy.com/system/resources/thumbnails/070/636/042/small/job-search-concept-laptop-phone-glasses-and-pen-on-wooden-desk-representing-career-opportunities-photo.jpeg"
                    alt="About"
                  />
                  <div className="hp-about-img-badge">
                    <span>95%</span>Match Accuracy
                  </div>
                </div>
              </div>
              <div className="col-lg-6 fade-up-2">
                <div className="hp-section-tag">About Us</div>
                <h2 className="hp-section-title">Recruitment Reimagined with Artificial Intelligence</h2>
                <p className="hp-about-text">
                  Welcome to <strong>AI Powered Shortlisted Jobs</strong> — a next-generation recruitment platform that eliminates guesswork from hiring. Our AI analyzes skills, experience, and preferences to surface only the most relevant opportunities.
                </p>
                <p className="hp-about-text">
                  We help candidates focus on roles that truly fit their profile, while giving employers faster, unbiased shortlisting with access to pre-qualified talent.
                </p>
                <ul className="hp-check-list">
                  <li>AI-driven skill matching across thousands of roles</li>
                  <li>Real-time application status tracking</li>
                  <li>Verified companies and transparent hiring</li>
                  <li>Zero irrelevant listings — only perfect fits</li>
                </ul>
                <Link to="/jobs" className="hp-btn-primary" style={{ display: "inline-block" }}>
                  Explore Opportunities →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section className="hp-features">
          <div className="container">
            <div className="hp-features-header fade-up">
              <div className="hp-section-tag">Why Choose Us</div>
              <h2 className="hp-section-title">Everything You Need to Land the Right Job</h2>
            </div>
            <div className="row g-4">
              {features.map((f, i) => (
                <div key={f.title} className="col-sm-6 col-lg-3">
                  <div className="hp-feature-card fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                    <div className="hp-feature-icon">{f.icon}</div>
                    <div className="hp-feature-title">{f.title}</div>
                    <p className="hp-feature-desc">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Job Categories ── */}
        <section className="hp-categories">
          <div className="container">
            <div className="text-center mb-5 fade-up">
              <div className="hp-section-tag">Browse by Category</div>
              <h2 className="hp-section-title">Explore Top Industries</h2>
            </div>
            <div className="row g-3">
              {categories.map((cat, i) => (
                <div key={cat.name} className="col-6 col-md-4 col-lg-2">
                  <Link to="/jobs" className="hp-cat-card fade-up" style={{ animationDelay: `${i * 0.07}s` }}>
                    <span className="hp-cat-icon">{cat.icon}</span>
                    <div className="hp-cat-name">{cat.name}</div>
                    <div className="hp-cat-count">{cat.count}</div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA Banner ── */}
        <section className="hp-cta">
          <div className="container hp-cta-inner">
            <h2 className="hp-cta-title">Ready to Find Your Next Role?</h2>
            <p className="hp-cta-sub">
              Join over 200,000 professionals who found their perfect match through our AI platform.
            </p>
            <div className="hp-hero-btns">
              <Link to="/jobs" className="hp-btn-primary">Browse All Jobs →</Link>
              <Link to="/about" className="hp-btn-outline">How It Works</Link>
            </div>
          </div>
        </section>

      </main>

      <WebFooter />
    </>
  );
}

export default Home;
